import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useFeatureGate from '../hooks/useFeatureGate';

/**
 * UserDropdown component with user info, admin links, and logout
 */
export default function UserDropdown() {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event) {
      /* c8 ignore start */
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
      /* c8 ignore stop */
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const collectionFeatureEnabled = useFeatureGate('feature:collection') && user?.tier?.features?.collection?.enabled !== false;
  const exportEnabled = useFeatureGate('feature:export_html') && user?.tier?.features?.export?.enabled !== false;
  const favoritesFeatureEnabled = useFeatureGate('feature:favorites') && user?.tier?.features?.favorites?.channelsEnabled !== false;

  if (!user) {
    return null;
  }

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User button with admin badge */}
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`User menu for ${user.displayName}`}
      >
        <img
          src={user.profileImageUrl}
          alt={user.displayName}
          className="w-8 h-8 rounded-full"
        />
        <span className="text-sm font-medium hidden sm:block">{user.displayName}</span>
        {user.isAdmin && (
          <span
            className="text-lg"
            role="img"
            aria-label="Admin"
            title="Administrator"
          >
            👑
          </span>
        )}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 bg-dark-700 border border-dark-600 rounded-lg shadow-lg z-50"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1">
            {/* User info section */}
            <div className="px-4 py-2 border-b border-dark-600">
              <p className="text-sm font-medium text-white">{user.displayName}</p>
              <p className="text-xs text-gray-400">@{user.twitchUsername}</p>
              {user.isAdmin && (
                <p className="text-xs text-primary-400 mt-1 flex items-center gap-1">
                  <span role="img" aria-label="Admin badge">👑</span>
                  Administrator
                </p>
              )}
            </div>

            {/* Admin Dashboard link (only for admins) */}
            {user.isAdmin && (
              <>
                <Link
                  to="/admin"
                  className="block px-4 py-2 text-sm text-white hover:bg-dark-600 transition-colors flex items-center gap-2"
                  role="menuitem"
                  onClick={() => setIsOpen(false)}
                >
                    <span role="img" aria-label="Dashboard">👑</span>
                  Admin Dashboard
                </Link>
                <div className="border-t border-dark-600" />
              </>
            )}

            {/* Search Clips link (respects tier settings) */}
            {exportEnabled && (
              <Link
                to="/dashboard"
                className={`block px-4 py-2 text-sm hover:bg-dark-600 transition-colors flex items-center gap-2 ${
                  location.pathname === '/dashboard' ? 'text-primary-400' : 'text-white'
                }`}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                <span role="img" aria-label="Search">🔎</span>
                Search
              </Link>
            )}
            {collectionFeatureEnabled && (
              <Link
                to="/collection"
                className={`block px-4 py-2 text-sm hover:bg-dark-600 transition-colors flex items-center gap-2 ${
                  location.pathname === '/collection' ? 'text-primary-400' : 'text-white'
                }`}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                <span role="img" aria-label="Collection">📂</span>
                Collection
              </Link>
            )}

            {/* Favorite Channels link (feature gated) */}
            {favoritesFeatureEnabled && (
              <Link
                to="/favorites"
                className={`block px-4 py-2 text-sm hover:bg-dark-600 transition-colors flex items-center gap-2 ${
                  location.pathname === '/favorites' ? 'text-primary-400' : 'text-white'
                }`}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                <span role="img" aria-label="Favorites">⭐</span>
                Favorite Channels
              </Link>
            )}

            {/* Settings link */}
            <Link
              to="/settings"
              className="block px-4 py-2 text-sm text-white hover:bg-dark-600 transition-colors flex items-center gap-2"
              role="menuitem"
              onClick={() => setIsOpen(false)}
            >
              <span role="img" aria-label="Settings">⚙️</span>
              Settings
            </Link>

            <div className="border-t border-dark-600" />

            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-dark-600 hover:text-white transition-colors"
              role="menuitem"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
